import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Scale, ArrowLeft, Plus, DollarSign, Users, X, UserCheck, Gavel, MapPin, Calendar, FileText } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";

const fmtInput = (v) => {
  const n = parseInt(String(v).replace(/\D/g, "")) || 0;
  return n.toLocaleString("es-CL");
};
const parseNum = (v) => parseInt(String(v).replace(/\D/g, "")) || 0;

function NuevoCaso({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [demandados, setDemandados] = useState([]);
  const [abogadosDir, setAbogadosDir] = useState([]);
  const [showNuevaEmpresa, setShowNuevaEmpresa] = useState(false);
  const [showNuevoDemandado, setShowNuevoDemandado] = useState(false);

  const [formData, setFormData] = useState({
    titulo_caso: "",
    empresa_cliente_id: "",
    demandado_id: "",
    tipo_demanda: "civil",
    tipo_conflicto: "Incumplimiento de pagos",
    descripcion_breve: "",
    usuario_responsable_id: user.id,
    abogados_asignados: [{ nombre: "", email: "" }],
    // Tribunal
    rit_rol: "",
    tribunal: "",
    numero_juzgado: "",
    materia: "",
    cuantia_demanda: 0,
    estado_procesal: "",
    // Fechas
    fecha_demanda: "",
    fecha_notificacion: "",
    fecha_audiencia_proxima: "",
    // Demandado
    domicilio_demandado: "",
    representante_legal_demandado: "",
    // Notas
    observaciones: "",
    // Valorización
    valorizacion: {
      monto_principal: 0,
      costo_inversion: 0,
      lucro_cesante: 0,
      dano_moral: 0,
      reajuste_ipc: 0,
      costas_procesales: 0
    }
  });

  const [nuevaEmpresa, setNuevaEmpresa] = useState({ nombre_legal: "", rut: "", tipo_persona: "juridica", contacto_principal_nombre: "", contacto_principal_email: "", contacto_principal_telefono: "" });
  const [nuevoDemandado, setNuevoDemandado] = useState({ nombre_legal: "", rut: "", tipo_persona: "juridica" });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [e, d, a] = await Promise.all([api.get("/empresas"), api.get("/demandados"), api.get("/abogados")]);
      setEmpresas(e.data); setDemandados(d.data); setAbogadosDir(a.data);
    } catch { toast.error("Error cargando datos"); }
  };

  const handleCreateEmpresa = async () => {
    try {
      const res = await api.post("/empresas", nuevaEmpresa);
      setEmpresas([...empresas, res.data]);
      setFormData({ ...formData, empresa_cliente_id: res.data.id });
      setShowNuevaEmpresa(false);
      toast.success("Empresa creada");
    } catch { toast.error("Error creando empresa"); }
  };

  const handleCreateDemandado = async () => {
    try {
      const res = await api.post("/demandados", nuevoDemandado);
      setDemandados([...demandados, res.data]);
      setFormData({ ...formData, demandado_id: res.data.id });
      setShowNuevoDemandado(false);
      toast.success("Demandado creado");
    } catch { toast.error("Error creando demandado"); }
  };

  const updateAbogado = (idx, field, value) => {
    const updated = [...formData.abogados_asignados];
    updated[idx] = { ...updated[idx], [field]: value };
    setFormData({ ...formData, abogados_asignados: updated });
  };
  const addAbogado = () => { if (formData.abogados_asignados.length < 3) setFormData({ ...formData, abogados_asignados: [...formData.abogados_asignados, { nombre: "", email: "" }] }); };
  const removeAbogado = (idx) => setFormData({ ...formData, abogados_asignados: formData.abogados_asignados.filter((_, i) => i !== idx) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titulo_caso || !formData.empresa_cliente_id || !formData.demandado_id) { toast.error("Completa los campos obligatorios"); return; }
    setLoading(true);
    try {
      const payload = { ...formData, abogados_asignados: formData.abogados_asignados.filter(a => a.nombre || a.email) };
      const res = await api.post("/casos", payload);
      toast.success("Caso creado exitosamente");
      navigate(`/casos/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error creando caso"); }
    finally { setLoading(false); }
  };

  const updateVal = (key, value) => {
    setFormData({ ...formData, valorizacion: { ...formData.valorizacion, [key]: parseNum(value) } });
  };

  const f = (field, value) => setFormData({ ...formData, [field]: value });

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <TopNav user={user} onLogout={onLogout} />
      <main className="container mx-auto px-6 py-6 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-xs" style={{ color: "var(--text-muted)" }}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
        </Button>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* === DATOS GENERALES === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Scale className="h-4 w-4" style={{ color: "var(--primary)" }} /> Datos Generales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Título del caso *</Label>
                <Input value={formData.titulo_caso} onChange={(e) => f("titulo_caso", e.target.value)} placeholder="Ej: Demanda incumplimiento contrato ABC Ltda." data-testid="titulo-caso-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Tipo de demanda</Label>
                  <Select value={formData.tipo_demanda} onValueChange={(v) => f("tipo_demanda", v)}>
                    <SelectTrigger data-testid="tipo-demanda-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="civil">Civil</SelectItem>
                      <SelectItem value="laboral">Laboral</SelectItem>
                      <SelectItem value="penal">Penal</SelectItem>
                      <SelectItem value="familia">Familia</SelectItem>
                      <SelectItem value="cobranza">Cobranza</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tipo de conflicto</Label>
                  <Input value={formData.tipo_conflicto} onChange={(e) => f("tipo_conflicto", e.target.value)} placeholder="Incumplimiento de pagos" data-testid="tipo-conflicto-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Materia</Label>
                <Input value={formData.materia} onChange={(e) => f("materia", e.target.value)} placeholder="Ej: Cobro de pesos, Cumplimiento de contrato" data-testid="materia-input" />
              </div>
              <div>
                <Label className="text-xs">Descripción breve</Label>
                <Textarea value={formData.descripcion_breve} onChange={(e) => f("descripcion_breve", e.target.value)} rows={2} placeholder="Descripción del caso" data-testid="descripcion-input" />
              </div>

              {/* Demandante (Empresa/Persona) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Demandante (empresa/persona) *</Label>
                  <button type="button" onClick={() => setShowNuevaEmpresa(true)} className="text-[10px] flex items-center gap-1" style={{ color: "var(--primary)" }}>
                    <Plus className="h-3 w-3" /> Nuevo
                  </button>
                </div>
                <Select value={formData.empresa_cliente_id} onValueChange={(v) => f("empresa_cliente_id", v)}>
                  <SelectTrigger data-testid="empresa-select"><SelectValue placeholder="Seleccionar demandante" /></SelectTrigger>
                  <SelectContent>
                    {empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre_legal} {e.rut ? `(${e.rut})` : ""} {e.tipo_persona === "natural" ? "- P.Natural" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Demandado/a */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Demandado/a (empresa/persona) *</Label>
                  <button type="button" onClick={() => setShowNuevoDemandado(true)} className="text-[10px] flex items-center gap-1" style={{ color: "var(--primary)" }}>
                    <Plus className="h-3 w-3" /> Nuevo
                  </button>
                </div>
                <Select value={formData.demandado_id} onValueChange={(v) => f("demandado_id", v)}>
                  <SelectTrigger data-testid="demandado-select"><SelectValue placeholder="Seleccionar demandado/a" /></SelectTrigger>
                  <SelectContent>
                    {demandados.map(d => <SelectItem key={d.id} value={d.id}>{d.nombre_legal} {d.rut ? `(${d.rut})` : ""} {d.tipo_persona === "natural" ? "- P.Natural" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* === DATOS DEL TRIBUNAL === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Gavel className="h-4 w-4" style={{ color: "var(--accent-blue)" }} /> Datos del Tribunal</CardTitle>
              <CardDescription className="text-xs">Información procesal del caso ante el tribunal</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">RIT / ROL</Label>
                  <Input value={formData.rit_rol} onChange={(e) => f("rit_rol", e.target.value)} placeholder="Ej: C-1234-2026" data-testid="rit-rol-input" />
                </div>
                <div>
                  <Label className="text-xs">Estado procesal</Label>
                  <Select value={formData.estado_procesal} onValueChange={(v) => f("estado_procesal", v)}>
                    <SelectTrigger data-testid="estado-procesal-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sin_definir">Sin definir</SelectItem>
                      <SelectItem value="presentada">Presentada</SelectItem>
                      <SelectItem value="admitida">Admitida</SelectItem>
                      <SelectItem value="notificada">Notificada</SelectItem>
                      <SelectItem value="contestacion">En contestación</SelectItem>
                      <SelectItem value="conciliacion">En conciliación</SelectItem>
                      <SelectItem value="prueba">Periodo de prueba</SelectItem>
                      <SelectItem value="sentencia">En sentencia</SelectItem>
                      <SelectItem value="apelacion">En apelación</SelectItem>
                      <SelectItem value="ejecutoriada">Ejecutoriada</SelectItem>
                      <SelectItem value="cumplimiento">En cumplimiento</SelectItem>
                      <SelectItem value="archivada">Archivada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Tribunal</Label>
                  <Select value={formData.tribunal} onValueChange={(v) => f("tribunal", v)}>
                    <SelectTrigger data-testid="tribunal-select"><SelectValue placeholder="Seleccionar tribunal" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sin_definir">Sin definir</SelectItem>
                      <SelectItem value="juzgado_civil">Juzgado Civil</SelectItem>
                      <SelectItem value="juzgado_letras">Juzgado de Letras</SelectItem>
                      <SelectItem value="juzgado_laboral">Juzgado de Letras del Trabajo</SelectItem>
                      <SelectItem value="juzgado_familia">Juzgado de Familia</SelectItem>
                      <SelectItem value="juzgado_garantia">Juzgado de Garantía</SelectItem>
                      <SelectItem value="juzgado_oral_penal">Tribunal Oral en lo Penal</SelectItem>
                      <SelectItem value="corte_apelaciones">Corte de Apelaciones</SelectItem>
                      <SelectItem value="corte_suprema">Corte Suprema</SelectItem>
                      <SelectItem value="juzgado_policia_local">Juzgado de Policía Local</SelectItem>
                      <SelectItem value="tribunal_arbitral">Tribunal Arbitral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Número / Nombre del Juzgado</Label>
                  <Input value={formData.numero_juzgado} onChange={(e) => f("numero_juzgado", e.target.value)} placeholder="Ej: 1er Juzgado Civil de Santiago" data-testid="juzgado-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Cuantía de la demanda (CLP)</Label>
                <Input value={formData.cuantia_demanda ? fmtInput(formData.cuantia_demanda) : ""} onChange={(e) => f("cuantia_demanda", parseNum(e.target.value))} placeholder="$0" data-testid="cuantia-input" />
              </div>
            </CardContent>
          </Card>

          {/* === FECHAS JUDICIALES === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" style={{ color: "var(--accent-amber)" }} /> Fechas Judiciales</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Fecha de demanda</Label>
                  <Input type="date" value={formData.fecha_demanda} onChange={(e) => f("fecha_demanda", e.target.value)} data-testid="fecha-demanda-input" />
                </div>
                <div>
                  <Label className="text-xs">Fecha de notificación</Label>
                  <Input type="date" value={formData.fecha_notificacion} onChange={(e) => f("fecha_notificacion", e.target.value)} data-testid="fecha-notificacion-input" />
                </div>
                <div>
                  <Label className="text-xs">Próxima audiencia</Label>
                  <Input type="date" value={formData.fecha_audiencia_proxima} onChange={(e) => f("fecha_audiencia_proxima", e.target.value)} data-testid="fecha-audiencia-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* === DATOS DEL DEMANDADO EN EL CASO === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" style={{ color: "var(--accent-rose)" }} /> Datos del Demandado en el Caso</CardTitle>
              <CardDescription className="text-xs">Información específica del demandado para este caso</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Representante legal del demandado</Label>
                <Input value={formData.representante_legal_demandado} onChange={(e) => f("representante_legal_demandado", e.target.value)} placeholder="Nombre del representante" data-testid="rep-legal-input" />
              </div>
              <div>
                <Label className="text-xs">Domicilio del demandado (para notificaciones)</Label>
                <Input value={formData.domicilio_demandado} onChange={(e) => f("domicilio_demandado", e.target.value)} placeholder="Dirección completa" data-testid="domicilio-input" />
              </div>
            </CardContent>
          </Card>

          {/* === ABOGADOS === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" style={{ color: "var(--accent-purple)" }} /> Abogados Asignados</CardTitle>
              <CardDescription className="text-xs">Asigna entre 1 y 3 abogados. Selecciona del directorio o agrega manualmente.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {abogadosDir.length > 0 && formData.abogados_asignados.length < 3 && (
                <div className="p-3 rounded-lg" style={{ background: "var(--bg)" }}>
                  <Label className="text-[10px] font-medium mb-2 block uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                    <UserCheck className="h-3 w-3 inline mr-1" />Del directorio
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {abogadosDir.filter(ad => !formData.abogados_asignados.some(a => a.email === ad.email)).map(ad => (
                      <button key={ad.id} type="button" onClick={() => {
                        setFormData({ ...formData, abogados_asignados: [...formData.abogados_asignados.filter(a => a.nombre || a.email), { nombre: ad.nombre, email: ad.email }] });
                      }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all hover:border-teal-300 hover:bg-teal-50" style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }} data-testid={`select-abogado-dir-${ad.id}`}>
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold" style={{ background: "var(--accent-blue)" }}>{ad.nombre[0]?.toUpperCase()}</div>
                        {ad.nombre}<Plus className="h-3 w-3 ml-1" style={{ color: "var(--text-muted)" }} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {formData.abogados_asignados.map((abogado, idx) => (
                <div key={idx} className="flex gap-3 items-end" data-testid={`abogado-row-${idx}`}>
                  <div className="flex-1">
                    <Label className="text-xs">Nombre</Label>
                    <Input value={abogado.nombre} onChange={(e) => updateAbogado(idx, "nombre", e.target.value)} placeholder="Nombre del abogado" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={abogado.email} onChange={(e) => updateAbogado(idx, "email", e.target.value)} placeholder="correo@ejemplo.cl" />
                  </div>
                  {formData.abogados_asignados.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeAbogado(idx)} className="text-red-500 hover:text-red-700 shrink-0"><X className="h-4 w-4" /></Button>
                  )}
                </div>
              ))}
              {formData.abogados_asignados.length < 3 && (
                <Button type="button" variant="outline" size="sm" onClick={addAbogado} className="mt-1 text-xs"><Plus className="h-3.5 w-3.5 mr-1" /> Agregar manualmente</Button>
              )}
            </CardContent>
          </Card>

          {/* === VALORIZACIÓN === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4" style={{ color: "var(--primary)" }} /> Valorización Económica (CLP)</CardTitle>
              <CardDescription className="text-xs">Montos estimados del caso. Puedes editarlos después.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: "monto_principal", label: "Monto Principal" },
                  { key: "costo_inversion", label: "Costo Inversión" },
                  { key: "lucro_cesante", label: "Lucro Cesante" },
                  { key: "dano_moral", label: "Daño Moral" },
                  { key: "reajuste_ipc", label: "Reajuste IPC" },
                  { key: "costas_procesales", label: "Costas Procesales" },
                ].map(m => (
                  <div key={m.key}>
                    <Label className="text-xs">{m.label}</Label>
                    <Input value={formData.valorizacion[m.key] ? fmtInput(formData.valorizacion[m.key]) : ""} onChange={(e) => updateVal(m.key, e.target.value)} placeholder="$0" data-testid={`val-${m.key}-input`} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* === OBSERVACIONES === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" style={{ color: "var(--text-muted)" }} /> Observaciones</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea value={formData.observaciones} onChange={(e) => f("observaciones", e.target.value)} rows={3} placeholder="Notas internas, comentarios, instrucciones especiales..." data-testid="observaciones-input" />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancelar</Button>
            <Button type="submit" disabled={loading} className="ls-btn-primary" data-testid="submit-caso-btn">
              {loading ? "Creando..." : "Crear Caso"}
            </Button>
          </div>
        </form>
      </main>

      {/* Dialog nuevo demandante */}
      <Dialog open={showNuevaEmpresa} onOpenChange={setShowNuevaEmpresa}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Nuevo Demandante</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Tipo de persona *</Label>
              <Select value={nuevaEmpresa.tipo_persona} onValueChange={(v) => setNuevaEmpresa({ ...nuevaEmpresa, tipo_persona: v })}>
                <SelectTrigger data-testid="demandante-tipo-persona"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="juridica">Persona Jurídica (Empresa)</SelectItem>
                  <SelectItem value="natural">Persona Natural</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">{nuevaEmpresa.tipo_persona === "natural" ? "Nombre completo *" : "Razón social *"}</Label><Input value={nuevaEmpresa.nombre_legal} onChange={(e) => setNuevaEmpresa({ ...nuevaEmpresa, nombre_legal: e.target.value })} placeholder={nuevaEmpresa.tipo_persona === "natural" ? "Juan Pérez López" : "Empresa S.A."} data-testid="demandante-nombre-input" /></div>
            <div><Label className="text-xs">RUT</Label><Input value={nuevaEmpresa.rut} onChange={(e) => setNuevaEmpresa({ ...nuevaEmpresa, rut: e.target.value })} placeholder="12.345.678-9" data-testid="demandante-rut-input" /></div>
            <div><Label className="text-xs">Contacto principal</Label><Input value={nuevaEmpresa.contacto_principal_nombre} onChange={(e) => setNuevaEmpresa({ ...nuevaEmpresa, contacto_principal_nombre: e.target.value })} data-testid="demandante-contacto-input" /></div>
            <div><Label className="text-xs">Email contacto</Label><Input value={nuevaEmpresa.contacto_principal_email} onChange={(e) => setNuevaEmpresa({ ...nuevaEmpresa, contacto_principal_email: e.target.value })} data-testid="demandante-email-input" /></div>
            <div><Label className="text-xs">Teléfono contacto</Label><Input value={nuevaEmpresa.contacto_principal_telefono} onChange={(e) => setNuevaEmpresa({ ...nuevaEmpresa, contacto_principal_telefono: e.target.value })} data-testid="demandante-telefono-input" /></div>
            <Button onClick={handleCreateEmpresa} className="w-full ls-btn-primary" data-testid="create-demandante-btn">Crear Demandante</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog nuevo demandado */}
      <Dialog open={showNuevoDemandado} onOpenChange={setShowNuevoDemandado}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Nuevo Demandado/a</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Tipo de persona *</Label>
              <Select value={nuevoDemandado.tipo_persona} onValueChange={(v) => setNuevoDemandado({ ...nuevoDemandado, tipo_persona: v })}>
                <SelectTrigger data-testid="demandado-tipo-persona"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="juridica">Persona Jurídica (Empresa)</SelectItem>
                  <SelectItem value="natural">Persona Natural</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">{nuevoDemandado.tipo_persona === "natural" ? "Nombre completo *" : "Razón social *"}</Label><Input value={nuevoDemandado.nombre_legal} onChange={(e) => setNuevoDemandado({ ...nuevoDemandado, nombre_legal: e.target.value })} placeholder={nuevoDemandado.tipo_persona === "natural" ? "Juan Pérez López" : "Empresa S.A."} data-testid="demandado-nombre-input" /></div>
            <div><Label className="text-xs">RUT</Label><Input value={nuevoDemandado.rut} onChange={(e) => setNuevoDemandado({ ...nuevoDemandado, rut: e.target.value })} placeholder="12.345.678-9" data-testid="demandado-rut-input" /></div>
            <Button onClick={handleCreateDemandado} className="w-full ls-btn-primary" data-testid="create-demandado-btn">Crear Demandado/a</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default NuevoCaso;
